<script setup>
import { SwSvgIcon } from '@getswish/design-library-web';
import { watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { useStore } from 'vuex';
import BankLogo from '@/_company/components/BankLogo.vue';
import CustomTransition from '@/_company/components/CustomTransition.vue';
import ErrorModal from '@/_company/components/ErrorModal.vue';
import PageLoader from '@/_company/components/PageLoader.vue';
import SidebarMenu from '@/_company/components/SidebarMenu.vue';
import TopBarComponent from '@/_company/components/TopBarComponent.vue';

const store = useStore();
const { locale } = useI18n();
const route = useRoute();
const router = useRouter();

function initLocale() {
  const newLocale = window.localStorage.getItem('locale') || store.state.locale.currentLocale;
  locale.value = newLocale;
  store.dispatch('locale/init', newLocale);
}

initLocale();

const user = computed(() => store.state.company.user.user);
const organizationName = computed(() => store.state.company.user.organizationName);
const isAuthenticating = computed(() => store.state.company.user.isAuthenticating);
const isSessionWarningVisible = computed(() => store.state.company.user.isSessionWarningVisible);

const showContent = computed(() => user.value || route.name === 'company-login');

function redirectToLoginPage() {
  if (route.name !== 'company-login') {
    const { path } = route;
    router.push({ name: 'company-login', query: { redirectPath: path } });
  }
}

async function checkUserStatus() {
  await store.dispatch('company/user/initUser');

  if (user.value) {
    return;
  }

  redirectToLoginPage();
}

checkUserStatus();
store.dispatch('company/settings/initSelectedPlatform');

function sessionExpiredConfirmed() {
  store.dispatch('company/user/setIsSessionWarningVisible', false);
  redirectToLoginPage();
}

function closeSidebar() {
  store.dispatch('common/closeSidebar');
}

watch(route, checkUserStatus);
</script>

<template>
  <main>
    <PageLoader v-if="isAuthenticating" />

    <CustomTransition v-else class="page-wrapper">
      <TopBarComponent v-if="user && !route.meta.fullscreen" />

      <div class="content-wrapper">
        <SidebarMenu v-if="user && !route.meta.fullscreen">
          <template v-slot:header>
            {{ organizationName || user.swishService.organizationNumber }}
          </template>
          <template v-slot:sub-header>{{ user.swishService.swishNumber }}</template>

          <template v-slot:links>
            <div class="links">
              <div @click="closeSidebar()">
                <RouterLink :to="{ name: 'certificates-list' }">
                  <SwSvgIcon name="licence" />
                  {{ $t('company.certificates.certificates') }}
                </RouterLink>
              </div>

              <div class="policy-link">
                <a href="https://www.swish.nu/policies" target="_blank">{{
                  $t('company.policy')
                }}</a>
              </div>
            </div>
          </template>

          <template v-slot:logo>
            <BankLogo />
          </template>
        </SidebarMenu>
        <div :class="['content', { 'full-width': route.meta.fullscreen }]" v-if="showContent">
          <router-view />
        </div>
      </div>
    </CustomTransition>

    <ErrorModal
      v-if="isSessionWarningVisible"
      :title="$t('company.sessionExpired.heading')"
      :text="$t('company.sessionExpired.text')"
      @onConfirm="sessionExpiredConfirmed"
    />
  </main>
</template>

<style lang="scss" scoped>
@include content;

.policy-link {
  margin: 0 (-$space-12);
  padding: 0 $space-12;
  border-top: 1px solid $secondary-border;

  @include tablet-large {
    padding: 0 $space-24;
    margin: 0 (-$space-24);
  }
}

.content-wrapper {
  background: $secondary-background;
}

.content {
  @include tablet-large {
    max-width: calc(100% - #{$sidebar-width});
  }
}

.links {
  a {
    @include secondary-text-regular;
    padding: $space-12 0;
    margin: calc($space-12 / 2) 0;
    display: flex;
    gap: $space-8;
    align-items: center;
    color: $primary-text;
    transition: color $transition-duration;

    :deep(path) {
      fill: $primary-text;
      transition: fill $transition-duration;
    }

    &.router-link-exact-active {
      @include secondary-text-medium;
      color: $primary-foreground;

      :deep(path) {
        fill: $primary-foreground;
      }
    }

    &:hover {
      color: $primary-foreground;

      :deep(path) {
        fill: $primary-foreground;
      }
    }
  }
}
</style>
